import { useAppSelector } from '../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import {
  useCreateNodeMutation,
  useDeleteNodeMutation,
  useGetEffectiveNodeQuery,
  useGetInputParametersHierarchyDefaultQuery,
  useUpdateNodeMutation,
} from '../../store/ai/inputParametersHierarchy'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { InputParameterHierarchyType, isMeetingAgendaInputParameters, MeetingAgendaInputParameters } from './model'
import { useZocksAuth, useZocksQuota } from '../ScopedBlock/hooks'
import { useGetAllUsersQuery } from '../../store/user/user'
import { User } from '../../models/User'
import { SelectOption } from '../SelectDropdown/SelectDropdown'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { InputParametersHierarchyId } from '../../models/InputParametersHierarchy'
import { MeetingTemplate } from '../../store/ai/meetingTemplate'
import { useAgendaFeature } from '../../apps/professional-app/fa/MeetingPreparation/Agenda/useAgendaFeature'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'

const MEETING_TEMPLATE_INPUT_HIERARCHY_LEVEL_ORDER = ['global', 'user', 'meeting_template_global', 'meeting_template_user']

export const useMeetingTemplateEffectiveInputParameters = (
  type: InputParameterHierarchyType,
  typeId: string,
  defaultInputParameters: { [key: string]: any } | null,
  userId?: string,
  meetingTemplateId?: string,
) => {
  const accountId = useAppSelector(selectAccountId)
  const { tracked } = useTracking()
  const { data: effectiveInputParameterHierarchyNode, isFetching: inputParametersLoading } = useGetEffectiveNodeQuery(
    {
      accountId,
      type,
      typeId,
      context: { effective: true, userId, meetingTemplateId },
    },
    { refetchOnMountOrArgChange: true, skip: !userId && !meetingTemplateId },
  )

  const expectedLevel = useMemo(() => {
    return userId && meetingTemplateId ? 'meeting_template_user' : userId ? 'user' : meetingTemplateId ? 'meeting_template_global' : 'global'
  }, [userId, meetingTemplateId])

  const parentLevel = useMemo(() => {
    const index = MEETING_TEMPLATE_INPUT_HIERARCHY_LEVEL_ORDER.indexOf(expectedLevel)
    return index > 0 ? MEETING_TEMPLATE_INPUT_HIERARCHY_LEVEL_ORDER[index - 1] : expectedLevel
  }, [expectedLevel])

  const { data: parentEffectiveInputParameterHierarchyNode, isFetching: parentInputParametersLoading } = useGetEffectiveNodeQuery(
    {
      accountId,
      type,
      typeId,
      context: { effective: true, userId, meetingTemplateId, level: parentLevel },
    },
    { refetchOnMountOrArgChange: true },
  )

  const [inputParameters, setInputParameters] = useState<{ [key: string]: any } | null>(null)
  const [parentInputParameters, setParentInputParameters] = useState<{ [key: string]: any } | null>(null)
  const [isCustomized, setIsCustomized] = useState<boolean>(false)
  const [updateLoading, setUpdateLoading] = useState<boolean>(false)

  const [_triggerCreateInputParametersHierarchyNode] = useCreateNodeMutation()
  const [_triggerUpdateInputParametersHierarchyNode] = useUpdateNodeMutation()
  const [_triggerDeleteInputParametersHierarchyNode] = useDeleteNodeMutation()

  const triggerCreateInputParametersHierarchyNode = tracked(_triggerCreateInputParametersHierarchyNode, {
    eventName: TrackEvent.INPUT_PARAMETERS_HIERARCHY_NODE_CREATED,
    eventDetails: {
      type,
      typeId,
      userId: userId ?? '',
      meetingTemplateId: meetingTemplateId ?? '',
      level: expectedLevel,
    },
  })

  const triggerUpdateInputParametersHierarchyNode = tracked(_triggerUpdateInputParametersHierarchyNode, {
    eventName: TrackEvent.INPUT_PARAMETERS_HIERARCHY_NODE_UPDATED,
    eventDetails: {
      type,
      typeId,
      userId: userId ?? '',
      meetingTemplateId: meetingTemplateId ?? '',
      nodeId: effectiveInputParameterHierarchyNode?.id ?? '',
      level: expectedLevel,
    },
  })

  const triggerDeleteInputParametersHierarchyNode = tracked(_triggerDeleteInputParametersHierarchyNode, {
    eventName: TrackEvent.INPUT_PARAMETERS_HIERARCHY_NODE_REMOVED,
    eventDetails: {
      type,
      typeId,
      userId: userId ?? '',
      meetingTemplateId: meetingTemplateId ?? '',
      nodeId: effectiveInputParameterHierarchyNode?.id ?? '',
      level: expectedLevel,
    },
  })

  useEffect(() => {
    setInputParameters(null)
    setParentInputParameters(null)
  }, [userId])

  useEffect(() => {
    if (inputParametersLoading) {
      return
    }
    if (effectiveInputParameterHierarchyNode) {
      const {
        inputParameters,
        hierarchyId: { level },
      } = effectiveInputParameterHierarchyNode
      setInputParameters(inputParameters)
      setIsCustomized(level === expectedLevel)
    } else {
      setInputParameters(defaultInputParameters)
      setIsCustomized(false)
    }
  }, [effectiveInputParameterHierarchyNode, inputParametersLoading, defaultInputParameters, expectedLevel])

  useEffect(() => {
    if (parentInputParametersLoading) {
      return
    }
    if (parentEffectiveInputParameterHierarchyNode) {
      const { inputParameters } = parentEffectiveInputParameterHierarchyNode
      setParentInputParameters(inputParameters)
    } else {
      setParentInputParameters(defaultInputParameters)
    }
  }, [parentEffectiveInputParameterHierarchyNode, parentInputParametersLoading, defaultInputParameters])

  const updateInputParameters = useCallback(
    async (inputParameters: { [key: string]: any }, isCustomized: boolean) => {
      setUpdateLoading(true)
      try {
        if (!isCustomized) {
          if (!effectiveInputParameterHierarchyNode) {
            return
          }
          const { id: nodeId } = effectiveInputParameterHierarchyNode
          await triggerDeleteInputParametersHierarchyNode({ accountId, type, typeId, nodeId })
        } else if (effectiveInputParameterHierarchyNode?.hierarchyId.level === expectedLevel) {
          const { id: nodeId } = effectiveInputParameterHierarchyNode
          await triggerUpdateInputParametersHierarchyNode({ accountId, type, typeId, nodeId, node: { inputParameters } })
        } else {
          const hierarchyId = { level: expectedLevel, userId, meetingTemplateId } as InputParametersHierarchyId
          await triggerCreateInputParametersHierarchyNode({
            accountId,
            type,
            typeId,
            node: { hierarchyId, inputParameters },
          })
        }
        setInputParameters(inputParameters)
        setIsCustomized(isCustomized)
      } catch (e) {
        console.error(e)
      } finally {
        setUpdateLoading(false)
      }
    },
    [
      accountId,
      effectiveInputParameterHierarchyNode,
      type,
      typeId,
      userId,
      meetingTemplateId,
      expectedLevel,
      triggerCreateInputParametersHierarchyNode,
      triggerUpdateInputParametersHierarchyNode,
      triggerDeleteInputParametersHierarchyNode,
    ],
  )

  return { isLoading: inputParametersLoading, inputParameters, parentInputParameters, updateLoading, updateInputParameters, isCustomized }
}

const EMAIL_TYPE_INPUT_HIERARCHY_LEVEL_ORDER = ['global', 'user', 'email_type_global', 'email_type_user']

export const useEmailTypeEffectiveInputParameters = (
  type: InputParameterHierarchyType,
  typeId: string,
  defaultInputParameters: { [key: string]: any } | null,
  userId?: string,
  emailTypeId?: string,
) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: effectiveInputParameterHierarchyNode, isFetching: inputParametersLoading } = useGetEffectiveNodeQuery(
    {
      accountId,
      type,
      typeId,
      context: { effective: true, userId, emailTypeId, contextType: 'email_type' },
    },
    { refetchOnMountOrArgChange: true, skip: !userId && !emailTypeId },
  )

  const expectedLevel = useMemo(() => {
    return userId && emailTypeId ? 'email_type_user' : userId ? 'user' : emailTypeId ? 'email_type_global' : 'global'
  }, [userId, emailTypeId])

  const parentLevel = useMemo(() => {
    const index = EMAIL_TYPE_INPUT_HIERARCHY_LEVEL_ORDER.indexOf(expectedLevel)
    return index > 0 ? EMAIL_TYPE_INPUT_HIERARCHY_LEVEL_ORDER[index - 1] : expectedLevel
  }, [expectedLevel])

  const { data: parentEffectiveInputParameterHierarchyNode, isFetching: parentInputParametersLoading } = useGetEffectiveNodeQuery(
    {
      accountId,
      type,
      typeId,
      context: { effective: true, userId, emailTypeId, level: parentLevel, contextType: 'email_type' },
    },
    { refetchOnMountOrArgChange: true },
  )

  const [inputParameters, setInputParameters] = useState<{ [key: string]: any } | null>(null)
  const [parentInputParameters, setParentInputParameters] = useState<{ [key: string]: any } | null>(null)
  const [isCustomized, setIsCustomized] = useState<boolean>(false)
  const [updateLoading, setUpdateLoading] = useState<boolean>(false)

  const [triggerCreateInputParametersHierarchyNode] = useCreateNodeMutation()
  const [triggerUpdateInputParametersHierarchyNode] = useUpdateNodeMutation()
  const [triggerDeleteInputParametersHierarchyNode] = useDeleteNodeMutation()

  useEffect(() => {
    setInputParameters(null)
    setParentInputParameters(null)
  }, [userId])

  useEffect(() => {
    if (inputParametersLoading) {
      return
    }
    if (effectiveInputParameterHierarchyNode) {
      const {
        inputParameters,
        hierarchyId: { level },
      } = effectiveInputParameterHierarchyNode
      setInputParameters(inputParameters)
      setIsCustomized(level === expectedLevel)
    } else {
      setInputParameters(defaultInputParameters)
      setIsCustomized(false)
    }
  }, [effectiveInputParameterHierarchyNode, inputParametersLoading, defaultInputParameters, expectedLevel])

  useEffect(() => {
    if (parentInputParametersLoading) {
      return
    }
    if (parentEffectiveInputParameterHierarchyNode) {
      const { inputParameters } = parentEffectiveInputParameterHierarchyNode
      setParentInputParameters(inputParameters)
    } else {
      setParentInputParameters(defaultInputParameters)
    }
  }, [parentEffectiveInputParameterHierarchyNode, parentInputParametersLoading, defaultInputParameters])

  const updateInputParameters = useCallback(
    async (inputParameters: { [key: string]: any }, isCustomized: boolean) => {
      setUpdateLoading(true)
      try {
        if (!isCustomized) {
          if (!effectiveInputParameterHierarchyNode) {
            return
          }
          const { id: nodeId } = effectiveInputParameterHierarchyNode
          await triggerDeleteInputParametersHierarchyNode({ accountId, type, typeId, nodeId })
        } else if (effectiveInputParameterHierarchyNode?.hierarchyId.level === expectedLevel) {
          const { id: nodeId } = effectiveInputParameterHierarchyNode
          await triggerUpdateInputParametersHierarchyNode({ accountId, type, typeId, nodeId, node: { inputParameters } })
        } else {
          const hierarchyId = { level: expectedLevel, userId, emailTypeId } as InputParametersHierarchyId
          await triggerCreateInputParametersHierarchyNode({
            accountId,
            type,
            typeId,
            node: { hierarchyId, inputParameters },
          })
        }
        setInputParameters(inputParameters)
        setIsCustomized(isCustomized)
      } catch (e) {
        console.error(e)
      } finally {
        setUpdateLoading(false)
      }
    },
    [
      accountId,
      effectiveInputParameterHierarchyNode,
      type,
      typeId,
      userId,
      emailTypeId,
      expectedLevel,
      triggerCreateInputParametersHierarchyNode,
      triggerUpdateInputParametersHierarchyNode,
      triggerDeleteInputParametersHierarchyNode,
    ],
  )

  return { isLoading: inputParametersLoading, inputParameters, parentInputParameters, updateLoading, updateInputParameters, isCustomized }
}

const DOCUMENT_TYPE_INPUT_HIERARCHY_LEVEL_ORDER = ['global', 'user', 'document_type_global', 'document_type_user']

export const useDocumentTypeEffectiveInputParameters = (
  type: InputParameterHierarchyType,
  typeId: string,
  defaultInputParameters: { [key: string]: any } | null,
  userId?: string,
  documentTypeId?: string,
) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: effectiveInputParameterHierarchyNode, isFetching: inputParametersLoading } = useGetEffectiveNodeQuery(
    {
      accountId,
      type,
      typeId,
      context: { effective: true, userId, documentTypeId, contextType: 'document_type' },
    },
    { refetchOnMountOrArgChange: true, skip: !userId && !documentTypeId },
  )

  const expectedLevel = useMemo(() => {
    return userId && documentTypeId ? 'document_type_user' : userId ? 'user' : documentTypeId ? 'document_type_global' : 'global'
  }, [userId, documentTypeId])

  const parentLevel = useMemo(() => {
    const index = DOCUMENT_TYPE_INPUT_HIERARCHY_LEVEL_ORDER.indexOf(expectedLevel)
    return index > 0 ? DOCUMENT_TYPE_INPUT_HIERARCHY_LEVEL_ORDER[index - 1] : expectedLevel
  }, [expectedLevel])

  const { data: parentEffectiveInputParameterHierarchyNode, isFetching: parentInputParametersLoading } = useGetEffectiveNodeQuery(
    {
      accountId,
      type,
      typeId,
      context: { effective: true, userId, documentTypeId, level: parentLevel, contextType: 'document_type' },
    },
    { refetchOnMountOrArgChange: true },
  )

  const [inputParameters, setInputParameters] = useState<{ [key: string]: any } | null>(null)
  const [parentInputParameters, setParentInputParameters] = useState<{ [key: string]: any } | null>(null)
  const [isCustomized, setIsCustomized] = useState<boolean>(false)
  const [updateLoading, setUpdateLoading] = useState<boolean>(false)

  const [triggerCreateInputParametersHierarchyNode] = useCreateNodeMutation()
  const [triggerUpdateInputParametersHierarchyNode] = useUpdateNodeMutation()
  const [triggerDeleteInputParametersHierarchyNode] = useDeleteNodeMutation()

  useEffect(() => {
    setInputParameters(null)
    setParentInputParameters(null)
  }, [userId])

  useEffect(() => {
    if (inputParametersLoading) {
      return
    }
    if (effectiveInputParameterHierarchyNode) {
      const {
        inputParameters,
        hierarchyId: { level },
      } = effectiveInputParameterHierarchyNode
      setInputParameters(inputParameters)
      setIsCustomized(level === expectedLevel)
    } else {
      setInputParameters(defaultInputParameters)
      setIsCustomized(false)
    }
  }, [effectiveInputParameterHierarchyNode, inputParametersLoading, defaultInputParameters, expectedLevel])

  useEffect(() => {
    if (parentInputParametersLoading) {
      return
    }
    if (parentEffectiveInputParameterHierarchyNode) {
      const { inputParameters } = parentEffectiveInputParameterHierarchyNode
      setParentInputParameters(inputParameters)
    } else {
      setParentInputParameters(defaultInputParameters)
    }
  }, [parentEffectiveInputParameterHierarchyNode, parentInputParametersLoading, defaultInputParameters])

  const updateInputParameters = useCallback(
    async (inputParameters: { [key: string]: any }, isCustomized: boolean) => {
      setUpdateLoading(true)
      try {
        if (!isCustomized) {
          if (!effectiveInputParameterHierarchyNode) {
            return
          }
          const { id: nodeId } = effectiveInputParameterHierarchyNode
          await triggerDeleteInputParametersHierarchyNode({ accountId, type, typeId, nodeId })
        } else if (effectiveInputParameterHierarchyNode?.hierarchyId.level === expectedLevel) {
          const { id: nodeId } = effectiveInputParameterHierarchyNode
          await triggerUpdateInputParametersHierarchyNode({ accountId, type, typeId, nodeId, node: { inputParameters } })
        } else {
          const hierarchyId = { level: expectedLevel, userId, documentTypeId } as InputParametersHierarchyId
          await triggerCreateInputParametersHierarchyNode({
            accountId,
            type,
            typeId,
            node: { hierarchyId, inputParameters },
          })
        }
        setInputParameters(inputParameters)
        setIsCustomized(isCustomized)
      } catch (e) {
        console.error(e)
      } finally {
        setUpdateLoading(false)
      }
    },
    [
      accountId,
      effectiveInputParameterHierarchyNode,
      type,
      typeId,
      userId,
      documentTypeId,
      expectedLevel,
      triggerCreateInputParametersHierarchyNode,
      triggerUpdateInputParametersHierarchyNode,
      triggerDeleteInputParametersHierarchyNode,
    ],
  )

  return { isLoading: inputParametersLoading, inputParameters, parentInputParameters, updateLoading, updateInputParameters, isCustomized }
}

export const useInputParametersConfig = () => {
  const { hasQuota } = useZocksQuota()
  const { hasScope } = useZocksAuth()

  const ipHierarchyEnabled = hasQuota('ai.ipHierarchy.value')
  const hasIpHierarchyAdminScope = hasScope('**', 'iph:admin')

  const { teamMembers, teamMembersLoading } = useTeamMembers()

  const hasTeamMembers = !!teamMembers.length
  const showTabs = hasIpHierarchyAdminScope && (hasTeamMembers || ipHierarchyEnabled)

  return {
    ipHierarchyEnabled,
    hasIpHierarchyAdminScope,
    showTabs,
    teamMembersLoading,
    hasTeamMembers,
  }
}

const useTeamMembers = () => {
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const { data: users, isFetching: teamMembersLoading } = useGetAllUsersQuery({ accountId, identityTypes: ['idp'] })

  const [teamMembers, setTeamMembers] = useState<User[]>([])

  useEffect(() => {
    if (!users) {
      return
    }

    const otherUsers = users.data.filter((u) => u.id !== currentUserId)

    setTeamMembers(otherUsers)
  }, [currentUserId, users])

  return {
    teamMembers,
    teamMembersLoading,
  }
}

export const useTeamMembersFilter = () => {
  const { teamMembers, teamMembersLoading } = useTeamMembers()

  const [userFilterValues, setUserFilterValues] = useState<SelectOption[]>([])
  const [selectedUserFilterOption, setSelectedUserFilterOption] = useState<SelectOption | null>(null)

  useEffect(() => {
    if (!teamMembers) {
      return
    }

    const teamMemberSelectOptions = teamMembers
      .sort((u1: User, u2: User) => {
        const userName1 = getUserDisplayName(u1)
        const userName2 = getUserDisplayName(u2)
        return userName1.localeCompare(userName2)
      })
      .map((u) => ({
        id: u.id,
        label: getUserDisplayName(u),
      }))

    setUserFilterValues(teamMemberSelectOptions)
  }, [teamMembers])

  return {
    userFilterValues,
    selectedUserFilterOption,
    setSelectedUserFilterOption,
    teamMembersLoading,
    hasTeamMembers: !!teamMembers.length,
  }
}

export const useInputParametersUpdateScope = (userId?: string) => {
  const { hasScope } = useZocksAuth()

  const hasIpHierarchyUpdateScope = hasScope(userId ?? '**', 'iph:update', ['iph:admin'])
  const hasIpHierarchyAdminScope = hasScope('**', 'iph:admin')
  const hasAiFeatureUpdateScope = hasScope('**', 'aifeature:update', ['aifeature:admin'])

  return {
    hasIpHierarchyUpdateScope,
    hasIpHierarchyAdminScope,
    hasAiFeatureUpdateScope,
  }
}

export const useAgendaInputHierarchyDefaults = (
  accountId: string,
  meetingTemplate?: MeetingTemplate,
): { data?: { inputParameters: MeetingAgendaInputParameters }; isLoading: boolean } => {
  const { checkIsAgendaEnabledForMeetingTemplate } = useAgendaFeature()
  const shouldFetchAgendaDefaults = checkIsAgendaEnabledForMeetingTemplate(meetingTemplate)

  const systemMeetingTemplateId = meetingTemplate?.systemMeetingTemplateId

  const { data: systemTemplateDefaults, isLoading: isLoadingSystemTemplate } = useGetInputParametersHierarchyDefaultQuery(
    {
      accountId,
      type: InputParameterHierarchyType.MEETING_AGENDA_CONFIG,
      typeId: systemMeetingTemplateId ?? '',
    },
    {
      skip: !shouldFetchAgendaDefaults || systemMeetingTemplateId == null,
    },
  )

  const { data: globalDefaults, isLoading: isLoadingGlobal } = useGetInputParametersHierarchyDefaultQuery(
    {
      accountId,
      type: InputParameterHierarchyType.MEETING_AGENDA_CONFIG,
      typeId: 'agenda-conf',
    },
    {
      skip: !shouldFetchAgendaDefaults,
    },
  )

  const data = systemTemplateDefaults || globalDefaults

  const isLoading = isLoadingSystemTemplate || isLoadingGlobal

  if (!data) {
    return { data, isLoading }
  }

  const inputParameters = data.inputParameters

  if (!isMeetingAgendaInputParameters(inputParameters)) {
    return { isLoading }
  }

  return { data: { inputParameters }, isLoading }
}

export const useUserInputParameterConfigForm = (
  inputParameters: { [key: string]: any },
  parentInputParameters: { [key: string]: any },
  isCustomized: boolean,
  onSave: (inputParameters: { [key: string]: any }, isCustomized: boolean) => Promise<void>,
  onHide: () => void,
) => {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [editedInputParameters, setEditedInputParameters] = useState<{ [key: string]: any }>(inputParameters)
  const [customized, setCustomized] = useState<boolean>(isCustomized)
  const [isChanged, setChanged] = useState<boolean>(false)

  useEffect(() => {
    setEditedInputParameters(inputParameters)
  }, [inputParameters])

  useEffect(() => {
    setCustomized(isCustomized)
  }, [isCustomized])

  const handleChangeCustomized = () => {
    setCustomized(!customized)
    if (customized) {
      setEditedInputParameters(parentInputParameters)
    } else {
      setEditedInputParameters(inputParameters)
    }
  }

  const handleClickClose = () => {
    if (isChanged) {
      setShowConfirmation(true)
    } else {
      onHide()
    }
  }

  const handleClickCancel = () => {
    setEditedInputParameters(inputParameters)
    setCustomized(isCustomized)
  }

  const handleClickSave = async () => {
    setChanged(false)
    await onSave(editedInputParameters, customized)
  }

  const checkForChange = useCallback(() => {
    setChanged(JSON.stringify(editedInputParameters) !== JSON.stringify(inputParameters) || customized !== isCustomized)
  }, [editedInputParameters, customized, inputParameters, isCustomized])

  return {
    editedInputParameters,
    customized,
    isChanged,
    showConfirmation,
    setEditedInputParameters,
    setShowConfirmation,
    handleChangeCustomized,
    handleClickCancel,
    handleClickSave,
    handleClickClose,
    checkForChange,
  }
}
